/**
 * Phase 270b — Always-on Onboarding Funnel Signal tile.
 *
 * Renders four headline numbers above the SA dashboard launcher cards:
 *   Stuck Step 1 (no students) · Step 2 (no coaches) · Step 3 (no fees) · Fully adopted
 *
 * Reuses the existing /super-admin/adoption/scan endpoint (preview mode)
 * — no new backend route, no new query patterns. Auto-loads on mount.
 */
import { useEffect, useState } from 'react';
import { ShieldAlert, Users, CreditCard, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';

const TILES = [
  {
    key: 'stuck_step_1_no_students',
    title: 'Step 1 — No students',
    subtitle: 'Trial signups stuck at zero athletes',
    Icon: ShieldAlert,
    tone: 'rose',
  },
  {
    key: 'stuck_step_2_no_coaches',
    title: 'Step 2 — No coaches',
    subtitle: 'Players added, no staff invited yet',
    Icon: Users,
    tone: 'amber',
  },
  {
    key: 'stuck_step_3_no_fees',
    title: 'Step 3 — No fees',
    subtitle: 'Roster live, billing not configured',
    Icon: CreditCard,
    tone: 'sky',
  },
  {
    key: 'fully_adopted',
    title: 'Fully adopted',
    subtitle: 'Running the complete Out-Play stack',
    Icon: CheckCircle2,
    tone: 'emerald',
  },
];

const TONE_CLASSES = {
  rose: 'border-rose-200 bg-rose-50/40 dark:border-rose-900 dark:bg-rose-950/20 text-rose-700 dark:text-rose-300',
  amber: 'border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300',
  sky: 'border-sky-200 bg-sky-50/40 dark:border-sky-900 dark:bg-sky-950/20 text-sky-700 dark:text-sky-300',
  emerald: 'border-emerald-200 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300',
};

const ICON_BG = {
  rose: 'bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-400',
  amber: 'bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400',
  sky: 'bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-400',
  emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400',
};

export default function OnboardingFunnelTile() {
  const [metrics, setMetrics] = useState(null);
  // Phase 272 — time-series deltas
  const [delta, setDelta] = useState(null);
  const [baselineAvailable, setBaselineAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.post('/super-admin/adoption/scan?since=7d');
        if (!alive) return;
        setMetrics(r.data?.metrics || null);
        setBaselineAvailable(Boolean(r.data?.historical_baseline_available));
        setDelta(r.data?.metrics_delta || null);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || 'Funnel signal unavailable');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const scrollToConsole = () => {
    const el = document.querySelector('[data-testid="adoption-nudge-console"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Card data-testid="onboarding-funnel-tile" className="border-2 border-indigo-100 dark:border-indigo-900">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold flex items-center gap-2">
              Onboarding funnel
              <span className="text-[9px] tracking-wider px-1.5 py-0.5 rounded bg-indigo-600 text-white font-bold">LIVE</span>
            </div>
            <p className="text-[11px] text-muted-foreground">Where your activation pipeline is leaking right now.</p>
          </div>
          {!loading && metrics && (
            <button
              type="button"
              onClick={scrollToConsole}
              className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
              data-testid="funnel-jump-to-console"
            >
              Open nudge console <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-6 text-center text-xs text-muted-foreground" data-testid="funnel-loading">
            <Loader2 className="h-4 w-4 mx-auto animate-spin mb-1" /> Computing funnel…
          </div>
        ) : error ? (
          <div className="py-4 text-center text-[11px] text-rose-600" data-testid="funnel-error">{error}</div>
        ) : metrics ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {TILES.map(({ key, title, subtitle, Icon, tone }) => (
              <div
                key={key}
                className={`rounded-xl border p-3 transition-all ${TONE_CLASSES[tone]}`}
                data-testid={`funnel-tile-${key}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">{title}</span>
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${ICON_BG[tone]}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                </div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-2xl font-black tracking-tight">{metrics[key] ?? 0}</span>
                  <span className="text-[10px] font-medium opacity-70">academies</span>
                </div>
                <p className="text-[10px] mt-0.5 opacity-70">{subtitle}</p>
                {/* Phase 272 — time-series delta vs 7 days ago */}
                {baselineAvailable && delta && typeof delta[key] === 'number' && (
                  <p
                    data-testid={`funnel-delta-${key}`}
                    className={`text-[10px] mt-1 font-bold tabular-nums ${
                      delta[key] === 0
                        ? 'text-muted-foreground'
                        : (tone === 'emerald'
                            ? (delta[key] > 0 ? 'text-emerald-600' : 'text-rose-600')
                            : (delta[key] > 0 ? 'text-rose-600' : 'text-emerald-600'))
                    }`}
                  >
                    {delta[key] > 0 ? '+' : ''}{delta[key]} vs last week
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : null}

        {!loading && metrics && metrics.stuck_actionable > 0 && (
          <p className="text-[11px] text-indigo-700 dark:text-indigo-300 font-medium" data-testid="funnel-actionable-hint">
            {metrics.stuck_actionable} of {metrics.stuck_total} stuck academies are off-cooldown and ready to nudge.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
